import { z } from "zod";

import type { FilterDefinition } from "./FilterDefinition";

export type FilterDataType =
  | "string"
  | "numeric"
  | "date"
  | "boolean"
  | "set"
  | "enum"
  // A user-created metric (dot-delimited path). Renders like `numeric` in the UI
  // (same comparators, value input, chip), but its `field` is a BARE path that
  // `filterToCondition` rewrites to `metric.<field>` — that prefixing is why the
  // variant exists. It's also distinct so a metric is distinguishable from a
  // numeric property (e.g. click-to-edit opens the metrics picker, not the form).
  | "metric";

/**
 * How distinct property filters join in the wire condition. `"AND"` (the
 * default) requires every filter to match; `"OR"` requires at least one.
 *
 * Lives in the filter schema (alongside `Filter` / `FilterDefinition`)
 * rather than on the table primitive because it's a filter concept, not a
 * table concept.
 */
export type FilterMatchMode = "AND" | "OR";

export const stringComparators = [
  "EQUALS",
  "NOT_EQUALS",
  "CONTAINS",
  "NOT_CONTAINS",
  "LIKE",
  "NOT_LIKE",
] as const;

export const numericComparators = [
  "EQUALS",
  "NOT_EQUALS",
  "GREATER_THAN",
  "LESS_THAN",
  "GREATER_THAN_OR_EQUAL",
  "LESS_THAN_OR_EQUAL",
] as const;

export const dateComparators = [
  "LESS_THAN",
  "GREATER_THAN",
  "BETWEEN",
] as const;

// Boolean fields: equality plus presence checks. Presence uses the same
// IS_NULL / IS_NOT_NULL pair as enum — the transpiler treats EXISTS ≡ IS_NOT_NULL
// on scalar columns, so this is identical wire behavior under one vocabulary.
export const booleanComparators = ["EQUALS", "IS_NOT_NULL", "IS_NULL"] as const;

// Set-membership fields (e.g. tags): the same CONTAINS/NOT_CONTAINS the
// backend's TAG_FIELD_COMPARATORS uses.
export const setComparators = ["CONTAINS", "NOT_CONTAINS"] as const;

// Single-valued, closed-option-set fields (a genuine enum, and custom Enum
// fields): equality plus presence checks — the same set as the backend's
// ENUM_FIELD_COMPARATORS. Order here is a UI choice (affirmative before
// negated in each pair: is / is not, is set / is not set), since this array
// drives the OperatorPicker layout. IS_NULL / IS_NOT_NULL are valueless.
export const enumComparators = [
  "EQUALS",
  "NOT_EQUALS",
  "IS_NOT_NULL",
  "IS_NULL",
] as const;

/**
 * Preset comparators are a valueless UI convenience.
 */
export const datePresets = [
  "TODAY",
  "LAST_7_DAYS",
  "LAST_30_DAYS",
  "LAST_90_DAYS",
  "THIS_MONTH",
] as const;

export const comparatorsByType = {
  string: stringComparators,
  numeric: numericComparators,
  date: dateComparators,
  boolean: booleanComparators,
  set: setComparators,
  enum: enumComparators,
  metric: numericComparators,
} as const satisfies Record<FilterDataType, readonly string[]>;

export const presetsByType = {
  string: [],
  numeric: [],
  date: datePresets,
  boolean: [],
  set: [],
  enum: [],
  metric: [],
} as const satisfies Record<FilterDataType, readonly string[]>;

type ComparatorsFor<T extends FilterDataType> =
  | (typeof comparatorsByType)[T][number]
  | (typeof presetsByType)[T][number];

export type StringComparator = ComparatorsFor<"string">;
export type NumericComparator = ComparatorsFor<"numeric">;
export type DateComparator = ComparatorsFor<"date">;
export type BooleanComparator = ComparatorsFor<"boolean">;
export type SetComparator = ComparatorsFor<"set">;
export type EnumComparator = ComparatorsFor<"enum">;

export type FilterComparator = ComparatorsFor<FilterDataType>;

/**
 * Subtype of `DateComparator` corresponding to the relative presets — the
 * narrow form `filterToCondition` accepts when expanding to a live
 * `now`-relative range.
 */
export type DatePresetComparator = (typeof datePresets)[number];

/**
 * All preset comparators across every field type — derived from
 * `presetsByType` so adding/reordering presets only touches one place.
 * Presets are valueless and resolved at query-emit time (e.g. date
 * relative presets expand to a `now`-relative range inside
 * `filterToCondition` so a saved filter stays live across days).
 */
export const PRESET_COMPARATORS: ReadonlySet<FilterComparator> = new Set(
  Object.values(presetsByType).flat(),
);

/**
 * Comparators that render no value input — the comparator alone carries
 * the semantic: `IS_NULL` / `IS_NOT_NULL` (enum and boolean presence
 * checks), plus every preset.
 */
export const VALUELESS_COMPARATORS: ReadonlySet<FilterComparator> = new Set([
  "IS_NULL",
  "IS_NOT_NULL",
  ...PRESET_COMPARATORS,
]);

/** True iff `comparator` is a valueless preset (e.g. date `TODAY`). */
export function isPreset(comparator: FilterComparator): boolean {
  return PRESET_COMPARATORS.has(comparator);
}

/**
 * True iff `comparator` renders no value input — the comparator alone
 * carries the semantic (`IS_NULL` / `IS_NOT_NULL`, plus every preset).
 * Used by FilterForm to decide whether to hide the value input row.
 */
export function isValueless(comparator: FilterComparator): boolean {
  return VALUELESS_COMPARATORS.has(comparator);
}

/**
 * Narrow guard used by `filterToCondition` to dispatch into the
 * date-specific preset→range expander. Distinct from the generic
 * `isPreset` because preset expansion logic is type-specific (today only
 * dates) and needs the narrowed `DatePresetComparator` for exhaustiveness.
 */
export function isDatePreset(
  comparator: FilterComparator,
): comparator is DatePresetComparator {
  return (datePresets as readonly string[]).includes(comparator);
}

const stringVariant = z.object({
  type: z.literal("string"),
  field: z.string().min(1),
  comparator: z.enum(stringComparators),
  values: z.array(z.string()),
});

const numericVariant = z.object({
  type: z.literal("numeric"),
  field: z.string().min(1),
  comparator: z.enum(numericComparators),
  values: z.array(z.number()),
});

const dateVariant = z.object({
  type: z.literal("date"),
  field: z.string().min(1),
  comparator: z.enum([...dateComparators, ...datePresets]),
  values: z.array(z.string()),
});

const booleanVariant = z.object({
  type: z.literal("boolean"),
  field: z.string().min(1),
  comparator: z.enum(booleanComparators),
  values: z.array(z.boolean()),
});

const setVariant = z.object({
  type: z.literal("set"),
  field: z.string().min(1),
  comparator: z.enum(setComparators),
  values: z.array(z.string()),
});

const enumVariant = z.object({
  type: z.literal("enum"),
  field: z.string().min(1),
  comparator: z.enum(enumComparators),
  values: z.array(z.string()),
});

// A metric filter is numeric-shaped; `field` holds the BARE dotted metric path
// (e.g. "navigation.path_deviation"). `filterToCondition` prepends the wire
// `metric.` prefix at conversion time.
const metricVariant = z.object({
  type: z.literal("metric"),
  field: z.string().min(1),
  comparator: z.enum(numericComparators),
  values: z.array(z.number()),
});

export const filterSchema = z
  .discriminatedUnion("type", [
    stringVariant,
    numericVariant,
    dateVariant,
    booleanVariant,
    setVariant,
    enumVariant,
    metricVariant,
  ])
  .superRefine((data, ctx) => {
    switch (data.type) {
      case "string":
        if (
          data.values.length === 0 ||
          !data.values.some((v) => v.trim().length > 0)
        ) {
          ctx.addIssue({
            code: "custom",
            path: ["values"],
            message: "Required",
          });
        }
        return;
      case "numeric":
      case "metric":
        if (
          data.values.length === 0 ||
          !data.values.every((n) => Number.isFinite(n))
        ) {
          ctx.addIssue({
            code: "custom",
            path: ["values"],
            message: "Required",
          });
        }
        return;
      case "date":
        // Relative presets are valueless — the comparator alone carries
        // the semantic.
        if (isDatePreset(data.comparator)) {
          return;
        }
        if (data.comparator === "BETWEEN") {
          if (
            data.values.length !== 2 ||
            data.values.some((v) => v.length === 0)
          ) {
            ctx.addIssue({
              code: "custom",
              path: ["values"],
              message: "Start and end dates required",
            });
          } else if (data.values[0] > data.values[1]) {
            // ISO 8601 strings (UTC midnight) compare lexicographically,
            // so plain `>` is a correct date ordering check here.
            ctx.addIssue({
              code: "custom",
              path: ["values"],
              message: "End date must be on or after start date",
            });
          }
        } else if (data.values.length === 0 || data.values[0].length === 0) {
          ctx.addIssue({
            code: "custom",
            path: ["values"],
            message: "Required",
          });
        }
        return;
      case "boolean":
        if (isValueless(data.comparator)) {
          return;
        }
        if (data.values.length !== 1) {
          ctx.addIssue({
            code: "custom",
            path: ["values"],
            message: "Pick true or false",
          });
        }
        return;
      case "set":
        if (data.values.length === 0) {
          ctx.addIssue({
            code: "custom",
            path: ["values"],
            message: "Pick at least one value",
          });
        }
        return;
      case "enum":
        // IS_NULL / IS_NOT_NULL are valueless; otherwise require a selection.
        if (isValueless(data.comparator)) {
          return;
        }
        if (data.values.length === 0) {
          ctx.addIssue({
            code: "custom",
            path: ["values"],
            message: "Pick at least one value",
          });
        }
        return;
    }
  });

/**
 * The canonical UI Filter shape
 */
export type Filter = z.infer<typeof filterSchema>;

/**
 * The comparators a field exposes in the OperatorPicker: the type's full
 * tuple, narrowed by the definition. `string` narrows via its optional
 * `allowedComparators`; `enum` and `boolean` expose their presence
 * operators (`IS_NOT_NULL` / `IS_NULL`) only when the field is `nullable`.
 * This is the single seam for per-field comparator narrowing —
 * `OperatorPicker` renders this list and `defaultComparatorFor` reads its
 * head, so the "default is always a visible comparator" invariant holds by
 * construction. (Date presets are layered on separately from `presetsByType`.)
 */
export function effectiveComparators(
  definition: FilterDefinition,
): readonly FilterComparator[] {
  switch (definition.type) {
    case "string":
      return definition.allowedComparators ?? stringComparators;
    case "numeric":
      return numericComparators;
    case "date":
      return dateComparators;
    case "set":
      return setComparators;
    case "enum":
      return definition.nullable ? enumComparators : ["EQUALS", "NOT_EQUALS"];
    case "boolean":
      return definition.nullable ? booleanComparators : ["EQUALS"];
  }
}

/** The comparator a freshly-picked field defaults to — the head of its visible set. */
export function defaultComparatorFor(
  definition: FilterDefinition,
): FilterComparator {
  return effectiveComparators(definition)[0];
}

/**
 * Defaults seeded into `useForm` when the user picks a field. Empty
 * `values` array — the refine fails until the user fills something in,
 * which keeps Add filter disabled. The comparator is the head of the
 * field's visible set (`defaultComparatorFor`), so it is always one the
 * OperatorPicker will show.
 */
export function getDefaultsForVariant(definition: FilterDefinition): Filter {
  // `defaultComparatorFor` returns a comparator valid for `definition.type`,
  // so the assembled (type, comparator) pair always matches one Filter
  // variant; the cast just re-narrows the umbrella types back to the union.
  return {
    type: definition.type,
    field: definition.field,
    comparator: defaultComparatorFor(definition),
    values: [],
  } as Filter;
}
